import { useState } from "react";
import { toast } from "sonner";
import { Search, Trash2, Plus, XCircle } from "lucide-react";
import { api, apiError, prettyDate } from "@/lib/api";

export default function ManageBooking() {
  const [code, setCode] = useState("");
  const [email, setEmail] = useState("");
  const [booking, setBooking] = useState(null);
  const [passengers, setPassengers] = useState([]);
  const [busy, setBusy] = useState(false);

  const lookup = async () => {
    if (!code.trim() || !email.trim()) return toast.error("Enter your confirmation number and email");
    setBusy(true);
    try {
      const { data } = await api.get("/bookings/lookup", { params: { code: code.trim(), email: email.trim() } });
      setBooking(data);
      setPassengers(data.passengers.map((p) => ({ ...p, age: String(p.age) })));
    } catch (e) {
      toast.error(apiError(e));
      setBooking(null);
    } finally {
      setBusy(false);
    }
  };

  const save = async () => {
    for (const p of passengers) {
      if (!p.name.trim() || p.age === "") return toast.error("Fill in every guest's name and age");
    }
    setBusy(true);
    try {
      const { data } = await api.put(`/bookings/${booking.code}`, {
        email: booking.email,
        passengers: passengers.map((p) => ({ name: p.name.trim(), age: parseInt(p.age, 10) })),
      });
      setBooking(data);
      setPassengers(data.passengers.map((p) => ({ ...p, age: String(p.age) })));
      toast.success("Booking updated — a confirmation email was sent");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const cancel = async () => {
    if (!window.confirm("Are you sure you want to cancel this booking?")) return;
    setBusy(true);
    try {
      await api.delete(`/bookings/${booking.code}`, { params: { email: booking.email } });
      setBooking({ ...booking, status: "cancelled" });
      toast.success("Booking cancelled");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f5fbfd] pt-28 pb-20 px-4">
      <div className="max-w-3xl mx-auto">
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-slate-900 mb-2">Manage Your Booking</h1>
        <p className="text-slate-600 mb-8">Enter your confirmation number and email to view, adjust or cancel.</p>

        <div className="bg-white rounded-3xl border border-cyan-100 shadow-xl p-6 sm:p-8 mb-8">
          <div className="flex flex-col sm:flex-row gap-3">
            <input
              data-testid="lookup-booking-input"
              className="flex-1 border border-slate-300 rounded-xl px-4 py-3 text-sm font-mono-code uppercase focus:outline-none focus:ring-2 focus:ring-sky-400"
              placeholder="Confirmation # (e.g. MMC-A1B2C)"
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
            />
            <input
              data-testid="lookup-email-input"
              type="email"
              className="flex-1 border border-slate-300 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-sky-400"
              placeholder="Email used for booking"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <button
              data-testid="lookup-booking-submit"
              onClick={lookup}
              disabled={busy}
              className="btn-ocean text-white font-bold px-7 py-3 rounded-xl flex items-center justify-center gap-2 disabled:opacity-60"
            >
              <Search size={16} /> Find
            </button>
          </div>
        </div>

        {booking && (
          <div data-testid="booking-details-card" className="bg-white rounded-3xl border border-cyan-100 shadow-xl p-6 sm:p-8 fade-up">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
              <div>
                <p className="font-mono-code text-2xl font-bold text-sky-600 tracking-widest">{booking.code}</p>
                <p className="text-sm text-slate-600 mt-1">
                  {booking.tour_name} · {prettyDate(booking.date)} · {booking.time}
                </p>
              </div>
              <span
                data-testid="booking-status-badge"
                className={`text-xs font-bold px-3 py-1.5 rounded-full ${
                  booking.status === "confirmed" ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-600"
                }`}
              >
                {booking.status.toUpperCase()}
              </span>
            </div>

            {booking.status === "cancelled" ? (
              <p className="text-sm text-slate-500 bg-slate-50 rounded-xl p-4 flex items-center gap-2">
                <XCircle size={16} className="text-red-400" /> This booking has been cancelled.
              </p>
            ) : (
              <>
                <div className="flex items-center justify-between mb-4">
                  <h2 className="font-display text-lg font-bold text-slate-900">Guest list ({passengers.length})</h2>
                  <button
                    data-testid="edit-passenger-button"
                    onClick={() => setPassengers([...passengers, { name: "", age: "" }])}
                    className="flex items-center gap-1.5 text-sm font-bold text-sky-600 hover:text-sky-700"
                  >
                    <Plus size={16} /> Add guest
                  </button>
                </div>
                <div className="space-y-3 mb-8">
                  {passengers.map((p, i) => (
                    <div key={i} className="flex gap-3 items-center">
                      <span className="text-xs font-bold text-slate-400 w-6">{i + 1}.</span>
                      <input
                        data-testid={`manage-passenger-name-${i}`}
                        className="flex-1 min-w-0 border border-slate-300 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-sky-400"
                        value={p.name}
                        onChange={(e) => setPassengers(passengers.map((x, idx) => (idx === i ? { ...x, name: e.target.value } : x)))}
                      />
                      <input
                        data-testid={`manage-passenger-age-${i}`}
                        type="number"
                        min="0"
                        className="w-16 sm:w-24 shrink-0 border border-slate-300 rounded-xl px-2 sm:px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-sky-400"
                        value={p.age}
                        onChange={(e) => setPassengers(passengers.map((x, idx) => (idx === i ? { ...x, age: e.target.value } : x)))}
                      />
                      <button
                        onClick={() => setPassengers(passengers.filter((_, idx) => idx !== i))}
                        className="text-slate-400 hover:text-red-500 transition-colors duration-150"
                      >
                        <Trash2 size={17} />
                      </button>
                    </div>
                  ))}
                </div>
                <div className="flex flex-wrap gap-3">
                  <button
                    data-testid="save-booking-button"
                    onClick={save}
                    disabled={busy}
                    className="btn-ocean text-white font-bold px-7 py-3 rounded-full disabled:opacity-60"
                  >
                    Save Changes
                  </button>
                  <button
                    data-testid="cancel-booking-button"
                    onClick={cancel}
                    disabled={busy}
                    className="border-2 border-red-300 text-red-600 font-bold px-7 py-3 rounded-full hover:bg-red-50 transition-colors duration-150 disabled:opacity-60"
                  >
                    Cancel Booking
                  </button>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
